import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useForm } from 'react-hook-form'

import { getUser, setUserInformations, setOrganizationDetails } from './state'

import { LookingToJoin, Main } from '../../../components'
import { ATTRIBUTION_OPTIONS, GOAL_OPTIONS } from './constants'
import { SelectOption } from '../../../components/Select/ControlledSelect'
import {
  Form,
  GoalSelect,
  MarketingAttributionSelect,
  DisplayNameInput,
} from '../../../components/Forms'
import { FormattedMessage, useIntl } from 'react-intl'

type UserStepFormInput = {
  displayName?: string
  goal: SelectOption
  marketingAttribution: SelectOption
}

export function UserStep({ superEnabled }: { superEnabled: boolean }) {
  const intl = useIntl()
  const user = useSelector(getUser)
  const hideDisplayName = user?.cacheKey

  const dispatch = useDispatch()
  const { handleSubmit, formState, control } = useForm<UserStepFormInput>({
    mode: 'onChange',
    defaultValues: {
      displayName: user?.displayName || '',
      goal: undefined,
      marketingAttribution: undefined,
    },
  })

  const onSubmit = (data: UserStepFormInput) => {
    const userInformations = {
      ...user,
      displayName: data.displayName ?? user?.displayName,
      acceptedPrivacyPolicy: true,
    }

    dispatch(
      setOrganizationDetails(
        {
          goal: data.goal.value,
          marketingAttribution: data.marketingAttribution.value,
        },
        false
      )
    )

    dispatch(setUserInformations(userInformations))
  }

  const goalOptionsInternationalized = GOAL_OPTIONS.map((option) => ({
    ...option,
    title: intl.formatMessage(option.title),
  }))

  const attributionOptionsInternationalized = ATTRIBUTION_OPTIONS.map(
    (option) => ({
      ...option,
      title: intl.formatMessage(option.title),
    })
  )

  return (
    <Main.Container footer={<LookingToJoin superEnabled={superEnabled} />}>
      <Main.Title>
        <FormattedMessage
          id="boarding.userStep.title"
          defaultMessage="Tell us about yourself"
          values={{}}
        />
      </Main.Title>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flexbox flexbox-column flexbox-grow form-grid"
      >
        {!hideDisplayName && (
          <div>
            <Form.Label>
              <FormattedMessage
                id="boarding.userStep.displayName"
                defaultMessage="Full name"
                values={{}}
              />
            </Form.Label>
            <DisplayNameInput<UserStepFormInput>
              name="displayName"
              control={control}
              autoFocus
              tabIndex={234234}
            />
          </div>
        )}

        {!superEnabled && (
          <div>
            <Form.Label>
              <FormattedMessage
                id="boarding.userStep.goal"
                defaultMessage="Your goal with Slite"
                values={{}}
              />
            </Form.Label>
            <GoalSelect<UserStepFormInput>
              control={control}
              name="goal"
              options={goalOptionsInternationalized}
            />
          </div>
        )}

        <div>
          <Form.Label>
            <FormattedMessage
              id="boarding.userStep.howDidYouHearAboutUs"
              defaultMessage="How did you hear about us?"
              values={{}}
            />
          </Form.Label>
          <MarketingAttributionSelect<UserStepFormInput>
            control={control}
            name="marketingAttribution"
            options={attributionOptionsInternationalized}
          />
        </div>

        <Form.Button disabled={!formState.isValid}>
          <FormattedMessage
            id="boarding.userStep.next"
            defaultMessage="Next"
            values={{}}
          />
        </Form.Button>
      </form>
    </Main.Container>
  )
}
